import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './NotreEquipe.css';

// Pôles et membres du cabinet — source : document « Direction du Cabinet ».
const POLES = [
  {
    key: 'direction',
    title: 'Direction du Cabinet',
    members: [
      {
        initials: 'SK',
        name: 'Salah Eddine EL KACIMI',
        suffix: 'MRICS',
        role: 'Expert Immobilier MRICS – Registered Valuer',
        credentials: [
          "Mastère en Droit et Fiscalité de l'Entreprise – ISCAE Casablanca",
          'Ingénieur Topographe – IAV Hassan II',
          "Enseignant des matières de l'immobilier (INAU, EHTP, etc.)",
        ],
      },
    ],
  },
  {
    key: 'commerciale',
    title: 'Direction Commerciale et de Contrôle de Qualité',
    members: [
      {
        initials: 'AT',
        name: 'Ali TAHIRI',
        role: 'Expert Judiciaire en Affaires Immobilières',
        credentials: [
          'Ingénieur Géomètre Topographe – IAV Hassan II',
          'Enseignant des matières du foncier et topographie',
        ],
      },
    ],
  },
  {
    key: 'evaluateurs',
    title: 'Évaluateurs Immobiliers – Consultants',
    members: [
      {
        initials: 'SA',
        name: 'Sakhr AMZIL',
        role: 'Évaluateur Immobilier, candidat RICS',
        credentials: [
          "Diplôme de l'ICH (CNAM — Strasbourg, France), cursus de référence accrédité par la RICS",
          'Ingénieur Géomètre Topographe – ESGT, Le Mans, France',
        ],
      },
      {
        initials: 'AC',
        name: 'Ayoub CHOQI',
        role: 'Évaluateur Immobilier, candidat RICS',
        credentials: [
          "Diplôme de l'ENCG (Casablanca) – Gestion Financière et Comptable",
        ],
      },
    ],
  },
  {
    key: 'terrain',
    title: 'Expertise Terrain & Études des Marchés',
    members: [
      {
        initials: 'SA',
        name: 'Said ANKOT',
        role: 'Responsable Terrain et Études des Marchés Immobiliers',
        credentials: ['Technicien Spécialisé en Topographie'],
      },
    ],
  },
];

const DIRECTION = POLES[0];
// Les 3 branches de l'organigramme sous le Directeur – Fondateur.
const BRANCHES = POLES.slice(1);

const NotreEquipe = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Notre Équipe | ATAA REALTY — Experts & Évaluateurs Immobiliers au Maroc"
        description="L'équipe d'ATAA REALTY : direction MRICS, direction commerciale et contrôle de qualité, évaluateurs immobiliers candidats RICS et pôle expertise terrain & études des marchés."
        canonical="https://ataarealty.com/notre-equipe"
        keywords="équipe ATAA Realty, expert immobilier MRICS Maroc, évaluateur immobilier Casablanca, organigramme cabinet expertise immobilière, candidat RICS Maroc"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: 'ATAA REALTY',
          url: 'https://ataarealty.com/',
          employee: [
            {
              '@type': 'Person',
              name: 'Salah Eddine El Kacimi',
              honorificSuffix: 'MRICS',
              jobTitle:
                'Directeur – Fondateur, Expert Immobilier MRICS – Registered Valuer',
            },
            {
              '@type': 'Person',
              name: 'Ali Tahiri',
              jobTitle:
                'Direction Commerciale et de Contrôle de Qualité — Expert Judiciaire en Affaires Immobilières',
            },
            {
              '@type': 'Person',
              name: 'Sakhr Amzil',
              jobTitle: 'Évaluateur Immobilier – Consultant, candidat RICS',
            },
            {
              '@type': 'Person',
              name: 'Ayoub Choqi',
              jobTitle: 'Évaluateur Immobilier – Consultant, candidat RICS',
            },
            {
              '@type': 'Person',
              name: 'Said Ankot',
              jobTitle: 'Responsable Terrain et Études des Marchés Immobiliers',
            },
          ],
        }}
      />
      <Navbar />

      {/* HERO */}
      <header className="equipe-header">
        <div className="equipe-overlay"></div>
        <div className="container equipe-title-box anim-up">
          <span className="equipe-subtitle">Organisation &amp; Compétences</span>
          <h1>Notre Équipe</h1>
        </div>
      </header>

      {/* INTRO */}
      <section className="equipe-intro-section">
        <div className="container">
          <div className="equipe-intro-grid">
            <div className="equipe-intro-text anim-up delay-1">
              <span className="intro-eyebrow">UNE ÉQUIPE PLURIDISCIPLINAIRE</span>
              <h2>
                Des compétences complémentaires au service de{' '}
                <span className="highlight">chaque mission</span>
              </h2>
            </div>
            <div className="equipe-intro-body anim-up delay-2">
              <p>
                <strong>ATAA REALTY</strong> réunit des profils complémentaires
                issus de l'ingénierie topographique, du droit et de la
                fiscalité, de la finance et des études de marché. Structurée
                autour de la direction du cabinet, l'équipe conduit chaque
                mission — expertise, conseil, études ou formation — avec la
                même exigence de rigueur, en alignement avec les standards{' '}
                <strong>RICS</strong> et les{' '}
                <strong>International Valuation Standards (IVS)</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ORGANIGRAMME */}
      <section className="equipe-org-section">
        <div className="container">
          <div className="equipe-org-header anim-up delay-1">
            <h5>ORGANISATION</h5>
            <h2>
              NOTRE <span>ORGANIGRAMME</span>
            </h2>
          </div>

          <div className="org-chart anim-up delay-2">
            {/* Racine : Directeur – Fondateur */}
            <div className="org-root">
              <span className="org-eyebrow">Directeur – Fondateur</span>
              <h3>
                {DIRECTION.members[0].name},{' '}
                <span className="org-mrics">{DIRECTION.members[0].suffix}</span>
              </h3>
              <p className="org-role">{DIRECTION.members[0].role}</p>
            </div>

            <div className="org-trunk"></div>

            {/* 3 branches sous le fondateur */}
            <div className="org-branches">
              {BRANCHES.map((pole) => (
                <div className="org-branch" key={pole.key}>
                  <div className="org-box">
                    <h4>{pole.title}</h4>
                    <p>{pole.members.map((m) => m.name).join(' · ')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ÉQUIPE PAR PÔLE */}
      <section className="equipe-members-section">
        <div className="container">
          {POLES.map((pole) => (
            <div className="equipe-pole anim-up delay-1" key={pole.key}>
              <h2 className="equipe-pole-title">{pole.title}</h2>
              <div className="equipe-members">
                {pole.members.map((m) => (
                  <div className="equipe-member-card" key={m.name}>
                    <div className="equipe-avatar">{m.initials}</div>
                    <div className="equipe-member-info">
                      <span className="equipe-member-name">
                        {m.name}
                        {m.suffix ? `, ${m.suffix}` : ''}
                      </span>
                      <span className="equipe-member-role">{m.role}</span>
                      <ul className="equipe-member-creds">
                        {m.credentials.map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="equipe-cta-section">
        <div className="container">
          <h3>Échanger avec notre équipe ?</h3>
          <p>
            Décrivez-nous votre projet : nos experts vous orienteront vers le
            pôle et le périmètre d'intervention les plus adaptés.
          </p>
          <Link to="/contact" className="equipe-cta-btn">
            Nous Contacter <FaArrowRight />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default NotreEquipe;
